import { Box, Typography, Container, Card, CardContent, Avatar, Stack, Chip } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import CentralBankIcon from "@mui/icons-material/AccountBalance";
import AutomationIcon from "@mui/icons-material/AutoFixHigh";
import ReductionIcon from "@mui/icons-material/TrendingDown";
import TeamIcon from "@mui/icons-material/Groups";
import InnovationIcon from "@mui/icons-material/Lightbulb";
import SupportIcon from "@mui/icons-material/SupportAgent";

export default function AboutPage({ theme }) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: <CentralBankIcon sx={{ fontSize: 32 }} />,
      title: "Centralização de Informações",
      description:
        "Acesse todos os dados importantes do seu negócio em um único lugar, simplificando sua gestão e tomada de decisões estratégicas.",
      color: "#1A73E8",
      gradient: "linear-gradient(135deg, #1A73E8 0%, #1557B0 100%)",
    },
    {
      icon: <AutomationIcon sx={{ fontSize: 32 }} />,
      title: "Automação Inteligente",
      description:
        "Reduza o trabalho manual automatizando processos repetitivos, permitindo que você foque no crescimento do seu negócio.",
      color: "#FF7323",
      gradient: "linear-gradient(135deg, #FF7323 0%, #D85A00 100%)",
    },
    {
      icon: <ReductionIcon sx={{ fontSize: 32 }} />,
      title: "Redução de Perdas",
      description:
        "Identifique gargalos no fluxo de trabalho e minimize perdas com relatórios inteligentes e ferramentas de análise avançada.",
      color: "#38A169",
      gradient: "linear-gradient(135deg, #38A169 0%, #2F855A 100%)",
    },
  ];

  const values = [
    {
      icon: <InnovationIcon sx={{ fontSize: 24 }} />,
      title: "Inovação Constante",
      description: "Sempre na vanguarda da tecnologia",
    },
    {
      icon: <TeamIcon sx={{ fontSize: 24 }} />,
      title: "Foco no Cliente",
      description: "Seu sucesso é nossa prioridade",
    },
    {
      icon: <SupportIcon sx={{ fontSize: 24 }} />,
      title: "Suporte Excepcional",
      description: "Estamos aqui quando você precisa",
    },
  ];

  return (
    <Box
      ref={sectionRef}
      sx={{
        py: { xs: 8, md: 12 },
        background: `
          linear-gradient(135deg, 
            rgba(255, 115, 35, 0.02) 0%, 
            rgba(255, 255, 255, 1) 50%, 
            rgba(26, 115, 232, 0.02) 100%
          )
        `,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background Decorations */}
      <Box
        sx={{
          position: "absolute",
          top: -100,
          left: -100,
          width: 300,
          height: 300,
          background:
            "radial-gradient(circle, rgba(26, 115, 232, 0.05) 0%, transparent 70%)",
          borderRadius: "50%",
          zIndex: 0,
        }}
      />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        {/* Header Section */}
        <Box
          sx={{
            textAlign: "center",
            mb: 8,
            transform: isVisible ? "translateY(0)" : "translateY(50px)",
            opacity: isVisible ? 1 : 0,
            transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <Chip
            label="🚀 Nossa História"
            sx={{
              mb: 3,
              backgroundColor: "rgba(26, 115, 232, 0.1)",
              color: theme.palette.secondary.main,
              fontWeight: 600,
              fontSize: "1rem",
              padding: "12px 20px",
              borderRadius: "50px",
              border: "1px solid rgba(26, 115, 232, 0.2)",
            }}
          />

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              fontWeight: 800,
              mb: 4,
              background:
                "linear-gradient(135deg, #1A73E8 0%, #FF7323 50%, #38A169 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Inovação que Transforma
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "1.2rem", md: "1.4rem" },
              color: theme.palette.text.secondary,
              maxWidth: "900px",
              mx: "auto",
              lineHeight: 1.8,
              mb: 6,
            }}
          >
            O{" "}
            <Box
              component="span"
              sx={{ fontWeight: 700, color: theme.palette.primary.main }}
            >
              Wala Smart Work
            </Box>{" "}
            é uma plataforma SaaS inovadora desenvolvida para{" "}
            <strong>revolucionar o e-commerce</strong>. Nossa solução centraliza
            informações essenciais, automatiza tarefas estratégicas e oferece
            insights poderosos para{" "}
            <strong>impulsionar os resultados do seu negócio</strong>.
          </Typography>
        </Box>

        {/* Mission Statement */}
        <Box
          sx={{
            textAlign: "center",
            mb: 10,
            p: 6,
            borderRadius: "24px",
            background: "rgba(255, 255, 255, 0.8)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 115, 35, 0.1)",
            boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.08)",
            transform: isVisible ? "scale(1)" : "scale(0.95)",
            opacity: isVisible ? 1 : 0,
            transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.2s",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: theme.palette.text.primary,
              fontSize: { xs: "1.8rem", md: "2.2rem" },
            }}
          >
            Nossa Missão
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: { xs: "1.2rem", md: "1.4rem" },
              lineHeight: 1.7,
              maxWidth: "800px",
              mx: "auto",
              fontStyle: "italic",
            }}
          >
            "Transformar cada e-commerce em uma máquina de crescimento inteligente,
            onde tecnologia e estratégia se unem para maximizar resultados e
            minimizar esforços."
          </Typography>
        </Box>

        {/* Features Grid */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            mb: 10,
            justifyContent: 'center'
          }}
        >
          {features.map((feature, index) => (
            <Box key={index} sx={{ flex: '1 1 350px', minWidth: '300px', maxWidth: '400px' }}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: "20px",
                  border: "1px solid rgba(255, 115, 35, 0.1)",
                  boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.06)",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  background: "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(20px)",
                  transform: isVisible
                    ? "translateY(0)"
                    : "translateY(30px)",
                  opacity: isVisible ? 1 : 0,
                  transitionDelay: `${index * 150}ms`,
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0px 20px 60px rgba(0, 0, 0, 0.12)",
                    "& .feature-icon": {
                      transform: "scale(1.1)",
                    },
                  },
                }}
              >
                <CardContent sx={{ p: 4, textAlign: "center" }}>
                  <Avatar
                    className="feature-icon"
                    sx={{
                      width: 80,
                      height: 80,
                      background: feature.gradient,
                      color: "white",
                      mx: "auto",
                      mb: 3,
                      transition: "transform 0.3s ease",
                    }}
                  >
                    {feature.icon}
                  </Avatar>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: theme.palette.text.primary,
                      fontSize: "1.3rem",
                    }}
                  >
                    {feature.title}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      lineHeight: 1.7,
                      fontSize: "1rem",
                    }}
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>

        {/* Values Section */}
        <Box
          sx={{
            textAlign: "center",
            mb: 8,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 6,
              color: theme.palette.text.primary,
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            Nossos Valores
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 3,
              justifyContent: 'center'
            }}
          >
            {values.map((value, index) => (
              <Box key={index} sx={{ flex: '1 1 280px', minWidth: '250px', maxWidth: '350px' }}>
                <Box
                  sx={{
                    p: 4,
                    borderRadius: "16px",
                    background: "rgba(255, 255, 255, 0.6)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 115, 35, 0.1)",
                    textAlign: "center",
                    transition: "all 0.3s ease",
                    transform: isVisible
                      ? "translateY(0)"
                      : "translateY(20px)",
                    opacity: isVisible ? 1 : 0,
                    transitionDelay: `${(index + 3) * 100}ms`,
                    "&:hover": {
                      transform: "translateY(-5px)",
                      background: "rgba(255, 255, 255, 0.9)",
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      background:
                        "linear-gradient(135deg, #FF7323 0%, #D85A00 100%)",
                      color: "white",
                      mx: "auto",
                      mb: 2,
                    }}
                  >
                    {value.icon}
                  </Avatar>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {value.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: "0.95rem",
                    }}
                  >
                    {value.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Closing Statement */}
        <Box
          sx={{
            textAlign: "center",
            transform: isVisible ? "translateY(0)" : "translateY(30px)",
            opacity: isVisible ? 1 : 0,
            transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.6s",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: { xs: "1.2rem", md: "1.4rem" },
              lineHeight: 1.7,
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            Com o{" "}
            <Box
              component="span"
              sx={{ fontWeight: 700, color: theme.palette.primary.main }}
            >
              Wala Smart Work
            </Box>
            , você transforma desafios em oportunidades e leva seu e-commerce ao
            próximo nível. Estamos aqui para ajudar você a{" "}
            <strong>crescer de forma eficiente e sustentável</strong>.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
